/**
 * Query keys convention:
 * - Orval-generated endpoints use their own keys (e.g. getGetHealthQueryKey() → ['/health']).
 * - For custom queries, use this namespace so keys are consistent and easy to invalidate.
 *
 * Pattern: [scope, ...args] e.g. ['app', 'user', userId]
 */
export const queryKeys = {
  /** App / non-API state */
  app: {
    all: ["app"] as const,
    user: (id: string) => ["app", "user", id] as const,
    settings: () => ["app", "settings"] as const,
  },
  /**
   * API keys – Orval generates per-endpoint keys (e.g. getGetHealthQueryKey).
   * Use this for custom API calls that aren't generated, or for invalidation groups.
   */
  api: {
    all: ["api"] as const,
    health: () => ["/health"] as const, // mirror Orval-generated key for reference
    chat: {
      all: ["api", "chat"] as const,
      list: (limit: number, offset: number) => ["api", "chat", "list", limit, offset] as const,
      latest: () => ["api", "chat", "latest"] as const,
      // Keep the alias so the latest-conversation cache reads naturally in the
      // hooks that hydrate the landing and response views.
      latestConversation: () => ["api", "chat", "latest"] as const,
      byId: (chatId: string) => ["api", "chat", chatId] as const,
    },
    planningItems: (params?: object) =>
      params == null
        ? (["api", "planning", "items"] as const)
        : (["api", "planning", "items", params] as const),
    planningWorkstreams: () => ["api", "planning", "workstreams"] as const,
    raidItems: (params?: object) =>
      params == null
        ? (["api", "raid", "items"] as const)
        : (["api", "raid", "items", params] as const),
    dependencyItems: (params?: object) =>
      params == null
        ? (["api", "dependencies", "items"] as const)
        : (["api", "dependencies", "items", params] as const),
    criticalGapLatestRun: () => ["api", "critical-gap-analysis", "runs", "latest"] as const,
    criticalGapDay1ReadinessContext: () =>
      ["api", "critical-gap-analysis", "context", "day1-readiness"] as const,
    criticalGapJobs: () => ["api", "critical-gap-analysis", "jobs"] as const,
    mappingReadiness: () => ["api", "mapping-readiness"] as const,
    tenantResetStatus: (resetJobId: string) => ["api", "tenant-reset", resetJobId] as const,
    tenantResetHistory: (skip: number, take: number) =>
      ["api", "tenant-reset", "history", skip, take] as const,
  },
} as const;

export type QueryKeyScope = keyof typeof queryKeys;
